import { useCallback, useMemo, useState } from 'react';

/**
 * City hero photos live in public/images/cities/ (e.g. "Halifax.webp"), NOT
 * under src/ — CRA copies public/ as-is, so there's no build-time way to
 * import or list them. Instead we build an ordered list of candidate URLs
 * (the city against each extension, then "Canada" against each extension
 * as the catch-all) and the <img>'s onError walks to the next candidate
 * until one loads. Add "<City>.webp" to public/images/cities/ to give a city
 * its own photo; any city without one shows Canada.
 */
// City photos are stored as .webp — tried first so the common case is a
// single request; the others are only a fallback for a newly added file.
const CITY_IMAGE_EXTENSIONS = ['webp', 'jpg', 'jpeg', 'png'];
const CITY_IMAGES_BASE = `${process.env.PUBLIC_URL}/images/cities`;
const FALLBACK_NAME = 'Canada';

const buildCityImageCandidates = cityName => {
  const candidates = [];
  if (cityName) {
    CITY_IMAGE_EXTENSIONS.forEach(ext => candidates.push(`${CITY_IMAGES_BASE}/${cityName}.${ext}`));
  }
  CITY_IMAGE_EXTENSIONS.forEach(ext => candidates.push(`${CITY_IMAGES_BASE}/${FALLBACK_NAME}.${ext}`));
  return candidates;
};

/**
 * Which city the hero photo should show, given a hero city picker's
 * committed selection: the picked city when exactly one is picked, the
 * Canada photo when several are, and the site-wide active city when
 * nothing has been picked yet.
 */
export const pickHeroCityName = (selectedCities, activeCityName) => {
  if (selectedCities && selectedCities.length === 1) return selectedCities[0].name;
  if (selectedCities && selectedCities.length > 1) return null;
  return activeCityName || null;
};

/**
 * Returns { src, onError } for the hero <img>. Starts at <city>.jpg and on
 * each onError advances (…jpeg, …png, …webp, then Canada.*) until one
 * loads. Resets whenever cityName changes.
 */
const useCityHeroImage = cityName => {
  const candidates = useMemo(() => buildCityImageCandidates(cityName), [cityName]);
  // Index is stored alongside the list it belongs to, so a new city starts
  // at its own first candidate on the very first render (no flash of the
  // previous city's fallback position).
  const [state, setState] = useState({ list: candidates, index: 0 });
  const index = state.list === candidates ? state.index : 0;

  const onError = useCallback(() => {
    setState(prev => {
      const current = prev.list === candidates ? prev.index : 0;
      return { list: candidates, index: Math.min(current + 1, candidates.length - 1) };
    });
  }, [candidates]);

  return { src: candidates[index], onError };
};

export default useCityHeroImage;
